import React, { useRef } from 'react';
import styled, { css } from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Keyboard, Navigation, Thumbs } from 'swiper';

import Picture from '@/components/Picture';
import ArrowIcon from '@/assets/svg/detailed-car/arrow.svg';
import {
  BigSliderProps,
  DetailedCarGalleryImageItem,
} from '@/components/DetailedCarGallery/DetailedCarGallery.types';
import MobileSlider from '@/components/DetailedCarGallery/components/MobileSlider';
import { colors } from '@/constants/theme';
SwiperCore.use([Navigation, Thumbs, Keyboard]);

function BigSlider({
  images,
  currentSlideIndex,
  setCurrentSlideIndex,
  thumbsSwiper,
  alt,
  isMobile,
  setSwiper,
  onClick,
}: BigSliderProps) {
  const prevNavButtonRef = useRef<HTMLDivElement>(null);
  const nextNavButtonRef = useRef<HTMLDivElement>(null);
  return (
    <>
      {isMobile ? (
        <>
          <MobileSlider
            images={images}
            currentSlideIndex={currentSlideIndex}
            setCurrentSlideIndex={setCurrentSlideIndex}
            onClick={onClick}
            setSwiper={setSwiper}
          />
        </>
      ) : (
        <Component>
          <Swiper
            onSwiper={setSwiper}
            loop={true}
            keyboard={{ enabled: true }}
            allowTouchMove={images?.length > 1}
            initialSlide={currentSlideIndex - 1}
            thumbs={{
              swiper:
                thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
            }}
            grabCursor={true}
            onSlideChange={(swiper) => setCurrentSlideIndex(swiper.activeIndex)}
            navigation={{
              prevEl: prevNavButtonRef.current,
              nextEl: nextNavButtonRef.current,
            }}
          >
            {images.map((item: DetailedCarGalleryImageItem, index) => (
              <SwiperSlide key={index}>
                <Slide onClick={onClick}>
                  <StyledPicture
                    desktop={{ src: item.desktop }}
                    tabletLarge={{ src: item.desktop }}
                    tabletSmall={{ src: item.desktop }}
                    loading="lazy"
                    alt={`${alt ?? 'detailed car image'} #${index + 1}`}
                  />
                </Slide>
              </SwiperSlide>
            ))}
            {images.length > 1 && (
              <>
                <StyledNavButtonWrapper ref={prevNavButtonRef} prev>
                  <ArrowIcon />
                </StyledNavButtonWrapper>

                <StyledNavButtonWrapper ref={nextNavButtonRef} next>
                  <ArrowIcon />
                </StyledNavButtonWrapper>
              </>
            )}
          </Swiper>
        </Component>
      )}
    </>
  );
}

export const Component = styled.div`
  position: relative;

  border-radius: 10px;
  padding-top: 55%;

  .swiper-container {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
  }
`;

export const Slide = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
`;

export const SlideCounter = styled.div`
  z-index: 3;
  position: absolute;
  bottom: 10px;
  left: 15px;
  display: inline-block;
  background-color: rgba(0, 0, 0, 0.65);
  color: ${colors.white};
  text-align: center;
  border-radius: 8px;
  font-weight: 500;
  font-size: 12px;
  line-height: 11px;
  padding: 5px;
`;

export const StyledNavButtonWrapper = styled.div<{
  next?: boolean;
  prev?: boolean;
}>`
  svg {
    path {
      fill: ${colors.white};
    }
  }

  display: flex;
  align-items: center;
  position: absolute;
  top: 0;
  bottom: 0;
  padding: 0 3%;
  z-index: 5;
  border-radius: 0 10px 10px 0;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  :hover {
    background-color: rgba(0, 0, 0, 0.3);
  }
  ${({ next }) =>
    next &&
    css`
      right: 0;
    `}
  ${({ prev }) =>
    prev &&
    css`
      left: 0;
      transform: rotate(180deg);
    `}
`;

export const StyledPicture = styled(Picture)`
  position: static !important;

  img,
  picture {
    z-index: 2;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    border-radius: 10px;
  }

  img {
    object-fit: cover;
  }
`;

export default BigSlider;
